// src/components/MobileNavBar.js
import React, { useState, useRef, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Package,
  Receipt,
  Wallet,
  MoreHorizontal,
  X,
  Lock,
  Truck,

  Box,
  Barcode,
  Globe,
  Shield,
  Settings,
  User,
  UserCircle,
  Search,
  Archive,
  FileText,
  Mail,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useGlobalSearch } from "./GlobalSearch";

const MobileNavBar = () => {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const location = useLocation();
  const { isAdmin } = useAuth();
  const globalSearch = useGlobalSearch();

  // Swipe-to-close state
  const panelRef = useRef(null);
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  // Touch handlers for swipe-to-close
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
    touchCurrentY.current = e.touches[0].clientY;
    setIsDragging(true);
    if (panelRef.current) {
      panelRef.current.style.transition = 'none';
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    touchCurrentY.current = e.touches[0].clientY;
    const deltaY = touchCurrentY.current - touchStartY.current;
    if (deltaY > 0 && panelRef.current) {
      const resistance = 0.6;
      panelRef.current.style.transform = `translateY(${deltaY * resistance}px)`;
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    const deltaY = touchCurrentY.current - touchStartY.current;
    const panel = panelRef.current;
    if (panel) {
      panel.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
      if (deltaY > 80) {
        panel.style.transform = 'translateY(100%)';
        setTimeout(() => {
          setMoreMenuOpen(false);
        }, 300);
      } else {
        panel.style.transform = 'translateY(0)';
      }
    }
    setIsDragging(false);
  }, [isDragging]);

  // Primary nav items (shown in bottom bar)
  const primaryNavItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Home" },
    { to: "/inventory", icon: Package, label: "Inventory" },
    { to: "/sales", icon: Receipt, label: "Sales" },
  ];

  // Quick access items for the More menu grid (includes Expenses)
  const quickAccessItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Home" },
    { to: "/inventory", icon: Package, label: "Inventory" },
    { to: "/sales", icon: Receipt, label: "Sales" },
    { to: "/expenses", icon: Wallet, label: "Expenses" },
  ];

  // Secondary nav items organized by sections
  const moreMenuSections = [
    {
      title: "Inventory",
      items: [
        { to: "/vault", icon: Lock, label: "Vault" },
        // { to: "/packages", icon: Box, label: "Packages" }, // Merged into Orders & Tracking
        { to: "/orders-tracking", icon: Truck, label: "Orders & Tracking" },
        { to: "/product-scanner", icon: Barcode, label: "My Products" },
      ],
    },
    // {
    //   title: "Tools",
    //   items: [
    //     { to: "/email-scraper", icon: Mail, label: "Email Scraper" },
    //   ],
    // },
    ...(isAdmin ? [{
      title: "Admin",
      items: [
        { to: "/global-products-admin", icon: Globe, label: "Global Products" },
        { to: "/admin-settings", icon: Shield, label: "Admin Settings" },
      ],
    }] : []),
    {
      title: "Account",
      items: [
        { to: "/account", icon: UserCircle, label: "Account" },
        { to: "/settings", icon: Settings, label: "Settings" },
        { to: "/reports", icon: FileText, label: "Reports" },
        { to: "/archives", icon: Archive, label: "Archives" },
      ],
    },
  ];

  // Flat list for checking if more is active (includes quick access items not in primary nav)
  const allMoreItems = [
    ...moreMenuSections.flatMap(section => section.items),
    { to: "/expenses" }, // Expenses moved from primary to More
  ];

  // Check if any "more" item is active
  const isMoreActive = allMoreItems.some(item => location.pathname === item.to);

  const NavItem = ({ to, icon: Icon, label, end = false, onClick }) => (
    <NavLink
      to={to}
      end={end}
      onClick={(e) => {
        // Only trigger onClick on primary (left) click, not middle-click
        if (onClick && e.button === 0 && !e.ctrlKey && !e.metaKey) {
          onClick();
        }
      }}
      className={({ isActive }) =>
        `mobile-nav-item flex flex-col items-center justify-center gap-1 py-2 px-3 rounded-xl transition-all
        ${isActive
          ? "text-[rgb(var(--accent-primary))] bg-[rgb(var(--accent-primary))]/10"
          : "text-theme-muted hover:text-theme"
        }`
      }
    >
      <Icon className="w-6 h-6" strokeWidth={1.75} />
      <span className="text-[11px] font-medium leading-tight">{label}</span>
    </NavLink>
  );

  const MoreMenuItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={(e) => {
        // Only close menu on primary (left) click, not middle-click
        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
          setMoreMenuOpen(false);
        }
      }}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 mx-1 rounded-xl transition-colors
        ${isActive
          ? "bg-[rgb(var(--accent-primary))]/10 text-[rgb(var(--accent-primary))]"
          : "text-theme hover:bg-theme-hover"
        }`
      }
    >
      <div className={`p-2 rounded-lg ${
        location.pathname === to
          ? "bg-[rgb(var(--accent-primary))]/15"
          : "bg-[var(--color-card-hover)]"
      }`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <span className="text-sm font-medium">{label}</span>
    </NavLink>
  );

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav
        className="mobile-bottom-nav fixed bottom-0 left-0 right-0 z-[1100] lg:hidden"
        style={{
          backgroundColor: 'var(--color-card)',
          borderTop: '1px solid var(--color-border)',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}
      >
        <div className="flex items-center justify-around px-2 py-2">
          {primaryNavItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}

          {/* Search button */}
          <button
            onClick={() => globalSearch.open()}
            className="mobile-nav-item flex flex-col items-center justify-center gap-1 py-2 px-3 rounded-xl transition-all text-theme-muted hover:text-theme"
          >
            <Search className="w-6 h-6" strokeWidth={1.75} />
            <span className="text-[11px] font-medium leading-tight">Search</span>
          </button>

          {/* More button */}
          <button
            onClick={() => setMoreMenuOpen(true)}
            className={`mobile-nav-item flex flex-col items-center justify-center gap-1 py-2 px-3 rounded-xl transition-all
              ${isMoreActive
                ? "text-[rgb(var(--accent-primary))] bg-[rgb(var(--accent-primary))]/10"
                : "text-theme-muted hover:text-theme"
              }`}
          >
            <MoreHorizontal className="w-6 h-6" strokeWidth={1.75} />
            <span className="text-[11px] font-medium leading-tight">More</span>
          </button>
        </div>
      </nav>

      {/* More Menu Overlay */}
      {createPortal(
        <AnimatePresence>
          {moreMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                className="fixed inset-0 z-[1150] bg-black/50 backdrop-blur-sm lg:hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMoreMenuOpen(false)}
              />

              {/* Menu Panel */}
              <motion.div
                ref={panelRef}
                className="fixed bottom-0 left-0 right-0 z-[1200] lg:hidden"
                style={{
                  backgroundColor: 'var(--color-card)',
                  borderRadius: '1.5rem 1.5rem 0 0',
                  paddingBottom: 'env(safe-area-inset-bottom, 0.5rem)',
                  maxHeight: '80vh',
                  boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.15)',
                }}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              >
                {/* Handle bar - swipe area */}
                <div
                  className="flex justify-center py-3 cursor-grab active:cursor-grabbing"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div className="w-10 h-1 rounded-full bg-[var(--color-border)]" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-4 pb-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <h3 className="text-base font-semibold text-theme">More</h3>
                  <button
                    onClick={() => setMoreMenuOpen(false)}
                    className="p-2 rounded-lg hover:bg-theme-hover text-theme-muted"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Menu Items */}
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 100px)' }}>
                  <div className="py-2">
                    {/* Quick Access - Core Nav Items */}
                    <div>
                      <div className="px-4 py-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-theme-muted">
                          Quick Access
                        </span>
                      </div>
                      <div className="px-2 grid grid-cols-4 gap-1">
                        {quickAccessItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = item.end
                            ? location.pathname === item.to
                            : location.pathname.startsWith(item.to);
                          return (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              end={item.end}
                              onClick={(e) => {
                                // Only close menu on primary (left) click, not middle-click
                                if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                                  setMoreMenuOpen(false);
                                }
                              }}
                              className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-colors
                                ${isActive
                                  ? "bg-[rgb(var(--accent-primary))]/10 text-[rgb(var(--accent-primary))]"
                                  : "text-theme-muted hover:bg-theme-hover hover:text-theme"
                                }`}
                            >
                              <Icon className="w-5 h-5" strokeWidth={1.75} />
                              <span className="text-[10px] font-medium">{item.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                      <div
                        className="mx-4 my-3"
                        style={{ height: '1px', backgroundColor: 'var(--color-border)' }}
                      />
                    </div>

                    {/* Other Sections */}
                    {moreMenuSections.map((section, sectionIndex) => (
                      <div key={section.title}>
                        {/* Section Header */}
                        <div className="px-4 py-2 mt-1 first:mt-0">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-theme-muted">
                            {section.title}
                          </span>
                        </div>
                        {/* Section Items */}
                        <div className="px-2">
                          {section.items.map((item) => (
                            <MoreMenuItem key={item.to} {...item} />
                          ))}
                        </div>
                        {/* Divider between sections */}
                        {sectionIndex < moreMenuSections.length - 1 && (
                          <div
                            className="mx-4 my-2"
                            style={{
                              height: '1px',
                              backgroundColor: 'var(--color-border)'
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default MobileNavBar;
